/**
 * Console Capture - Intercepts console methods and maintains a circular buffer
 */
export class ConsoleCapture {
    /**
     * @param {number} maxEntries - Maximum number of log entries to keep (default 50)
     */
    constructor(maxEntries = 50) {
        this.maxEntries = maxEntries;
        this.entries = [];
        this.originalConsole = {};
        this.isCapturing = false;
    }

    /**
     * Start capturing console output
     */
    start() {
        if (this.isCapturing) return;
        this.isCapturing = true;

        const methods = ['log', 'warn', 'error', 'info'];

        methods.forEach(method => {
            this.originalConsole[method] = console[method];
            console[method] = (...args) => {
                this.addEntry(method, args);
                this.originalConsole[method].apply(console, args);
            };
        });
    }

    /**
     * Stop capturing and restore original console methods
     */
    stop() {
        if (!this.isCapturing) return;
        this.isCapturing = false;

        Object.keys(this.originalConsole).forEach(method => {
            console[method] = this.originalConsole[method];
        });
        this.originalConsole = {};
    }

    /**
     * Add an entry to the buffer
     * @param {string} level - Log level (log, warn, error, info)
     * @param {Array} args - Arguments passed to console method
     */
    addEntry(level, args) {
        const message = args.map(arg => {
            if (typeof arg === 'object') {
                try {
                    return JSON.stringify(arg);
                } catch {
                    return String(arg);
                }
            }
            return String(arg);
        }).join(' ');

        const entry = {
            level,
            message,
            timestamp: new Date().toISOString()
        };

        this.entries.push(entry);

        // Maintain circular buffer
        if (this.entries.length > this.maxEntries) {
            this.entries.shift();
        }
    }

    /**
     * Get all captured entries
     * @returns {Array} Copy of entries array
     */
    getEntries() {
        return [...this.entries];
    }

    /**
     * Clear all entries
     */
    clear() {
        this.entries = [];
    }
}

// Singleton instance for app-wide use
export const consoleCapture = new ConsoleCapture(50);
